import { useState } from "react";
import { HowToReachUs } from "@/components/contact/HowToReachUs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Send,
  CheckCircle,
  Instagram,
  Facebook,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_WA_NUMBER = "917624853840";

const fabricOptions = [
  "600 Lining (Cambric)",
  "600 Gold (Premium Single Dyed)",
  "060 Lining (Double Dyed Cambric)",
  "006 Lining (Mercerized Cambric)",
  "6000 Shrinkless (Big Panna)",
  "Super Queen",
  "303 Pure Cotton",
  "3000 Pure Cotton (Big Panna)",
  "American Crepe",
  "Multiple Qualities",
  "Custom Requirement",
];

const buildWhatsAppLink = (form) => {
  const lines = [
    "*New Inquiry — Mutha Paras & Co.*",
    "",
    `*Name:* ${form.name || "-"}`,
    `*Email:* ${form.email || "-"}`,
    `*Phone:* ${form.phone || "-"}`,
    `*Fabric Interest:* ${form.fabric_interest || "-"}`,
    "",
    `*Message:*`,
    form.message || "-",
    "",
    "_Sent via muthaparas.com contact form_",
  ];
  return `https://wa.me/${ADMIN_WA_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
};

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    fabric_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [waLink, setWaLink] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    const link = buildWhatsAppLink(form);

    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) throw new Error("Failed to submit inquiry");

      setSubmitted(true);
      setWaLink(link);
      // Auto-open WhatsApp in a new tab with the pre-filled message
      window.open(link, "_blank", "noopener");
      setForm({ name: "", email: "", phone: "", fabric_interest: "", message: "" });
    } catch (err) {
      setError("Something went wrong. Please try again or contact us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 lg:py-32"
      style={{ backgroundColor: "var(--mp-surface)" }}
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-14">
          <p
            className="text-xs uppercase tracking-[0.2em] font-semibold mb-4"
            style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
          >
            Get in Touch
          </p>
          <h1
            className="text-3xl md:text-4xl tracking-tight leading-tight font-medium mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            data-testid="contact-heading"
          >
            Contact & Orders
          </h1>
          <div className="section-divider mx-auto" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left - Contact Info */}
          <div>
            <h3
              className="text-xl md:text-2xl tracking-tight font-medium mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            >
              Connect With Us
            </h3>

            <div className="space-y-6">
              {/* WhatsApp & Telegram */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://wa.me/917624853840"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                  data-testid="contact-whatsapp-btn"
                >
                  <Button
                    className="w-full text-white text-sm font-medium py-6 rounded-sm whatsapp-pulse"
                    style={{ backgroundColor: '#25D366', fontFamily: "'Manrope', sans-serif" }}
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Order via WhatsApp
                  </Button>
                </a>
                <a
                  href="https://t.me/+917624853840"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                  data-testid="contact-telegram-btn"
                >
                  <Button
                    className="w-full text-white text-sm font-medium py-6 rounded-sm"
                    style={{ backgroundColor: '#0088cc', fontFamily: "'Manrope', sans-serif" }}
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Order via Telegram
                  </Button>
                </a>
              </div>

              {/* Phone */}
              <div className="pastel-green border border-[var(--mp-line)] rounded-sm p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#10101015' }}>
                    <Phone className="w-5 h-5" style={{ color: "var(--mp-text-strong)" }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-1" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>
                      Phone
                    </p>
                    <a href="tel:+917624853840" className="text-sm block mb-0.5" style={{ color: "var(--mp-text-strong)", fontFamily: "'Manrope', sans-serif" }}>
                      +91 7624853840
                    </a>
                    <a href="tel:08022200274" className="text-sm block" style={{ color: "var(--mp-text-strong)", fontFamily: "'Manrope', sans-serif" }}>
                      080 22200274 (Landline)
                    </a>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="pastel-peach border border-[var(--mp-line)] rounded-sm p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#B8733315' }}>
                    <Mail className="w-5 h-5" style={{ color: "var(--mp-copper)" }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-1" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>
                      Email
                    </p>
                    <a href="mailto:muthaparas1975@gmail.com" className="text-sm block mb-0.5" style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}>
                      muthaparas1975@gmail.com (Orders)
                    </a>
                    <a href="mailto:muthaparas1975@hotmail.com" className="text-sm block" style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}>
                      muthaparas1975@hotmail.com (Queries)
                    </a>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="pastel-lavender border border-[var(--mp-line)] rounded-sm p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#8A5A2415' }}>
                    <Instagram className="w-5 h-5" style={{ color: "var(--mp-copper-deep)" }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold mb-2" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>
                      Follow Us
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <a
                        href="https://instagram.com/theliningstoreindia"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-instagram-btn"
                      >
                        <Button
                          variant="outline"
                          className="text-sm font-medium px-4 py-2 rounded-sm"
                          style={{ borderColor: '#E1306C', color: '#E1306C', fontFamily: "'Manrope', sans-serif" }}
                        >
                          <Instagram className="w-4 h-4 mr-2" />
                          @theliningstoreindia
                        </Button>
                      </a>
                      <a
                        href="https://facebook.com/muthaparas"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-facebook-btn"
                      >
                        <Button
                          variant="outline"
                          className="text-sm font-medium px-4 py-2 rounded-sm"
                          style={{ borderColor: '#1877F2', color: '#1877F2', fontFamily: "'Manrope', sans-serif" }}
                        >
                          <Facebook className="w-4 h-4 mr-2" />
                          muthaparas
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="pastel-blue border border-[var(--mp-line)] rounded-sm p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#5C534715' }}>
                    <MapPin className="w-5 h-5" style={{ color: '#5C5347' }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-1" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>
                      Visit Us (By Appointment)
                    </p>
                    <p className="text-sm leading-relaxed" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                      Kochaar Muthas Enclave No. 28,
                      <br />
                      Venkusahuji Lane, Chickpet Cross,
                      <br />
                      Bangalore - 560053, India
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Inquiry Form */}
          <div>
            <h3
              className="text-xl md:text-2xl tracking-tight font-medium mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            >
              Send an Inquiry
            </h3>

            {submitted ? (
              <div
                className="pastel-green border border-[var(--mp-line)] rounded-sm p-10 text-center animate-fade-in"
                data-testid="inquiry-success"
              >
                <CheckCircle className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--mp-text-strong)" }} />
                <h4
                  className="text-xl font-medium mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text-strong)" }}
                >
                  Inquiry Received!
                </h4>
                <p className="text-sm mb-2" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                  Your inquiry is saved with our team.
                </p>
                <p className="text-sm mb-6" style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}>
                  A WhatsApp window has opened with your details — please tap <strong>Send</strong> there to deliver it directly to our team for the fastest response.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {waLink && (
                    <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="inquiry-success-wa-link">
                      <Button
                        className="rounded-sm text-sm px-6 py-2 text-white whatsapp-pulse"
                        style={{ backgroundColor: '#25D366', fontFamily: "'Manrope', sans-serif" }}
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Open WhatsApp
                      </Button>
                    </a>
                  )}
                  <Button
                    onClick={() => { setSubmitted(false); setWaLink(""); }}
                    variant="outline"
                    className="rounded-sm text-sm px-6 py-2"
                    style={{ borderColor: "var(--mp-solid)", color: "var(--mp-text-strong)", fontFamily: "'Manrope', sans-serif" }}
                    data-testid="send-another-btn"
                  >
                    Send Another Inquiry
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="inquiry-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider block mb-2" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                      Full Name *
                    </label>
                    <Input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name"
                      className="rounded-sm border-[var(--mp-line)] bg-white/70 py-5 text-sm"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider block mb-2" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                      Email *
                    </label>
                    <Input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="your@email.com"
                      className="rounded-sm border-[var(--mp-line)] bg-white/70 py-5 text-sm"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider block mb-2" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                      Phone
                    </label>
                    <Input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+91 XXXXXXXXXX"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      className="rounded-sm border-[var(--mp-line)] bg-white/70 py-5 text-sm"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider block mb-2" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                      Fabric Interest
                    </label>
                    <Select
                      value={form.fabric_interest}
                      onValueChange={(val) => setForm({ ...form, fabric_interest: val })}
                    >
                      <SelectTrigger
                        className="rounded-sm border-[var(--mp-line)] bg-white/70 py-5 text-sm"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                        data-testid="select-fabric"
                      >
                        <SelectValue placeholder="Select fabric type" />
                      </SelectTrigger>
                      <SelectContent data-testid="select-fabric-content">
                        {fabricOptions.map((opt) => (
                          <SelectItem key={opt} value={opt} data-testid={`fabric-option-${opt.replace(/\s/g, '-').toLowerCase()}`}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider block mb-2" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                    Message *
                  </label>
                  <Textarea
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us about your requirements, quantities needed, specific colours..."
                    rows={5}
                    className="rounded-sm border-[var(--mp-line)] bg-white/70 text-sm resize-none"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                    data-testid="input-message"
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-600" data-testid="inquiry-error">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full text-white text-sm font-medium py-6 rounded-sm"
                  style={{ backgroundColor: "var(--mp-solid)", fontFamily: "'Manrope', sans-serif" }}
                  data-testid="submit-inquiry-btn"
                >
                  {submitting ? "Sending..." : "Send Inquiry & Open WhatsApp"}
                  {!submitting && <Send className="w-4 h-4 ml-2" />}
                </Button>
                <p
                  className="text-xs text-center"
                  style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                >
                  After submitting, WhatsApp will open with your details pre-filled — tap <strong>Send</strong> to deliver it directly to our team.
                </p>
              </form>
            )}
          </div>
        </div>

        {/* Google Maps */}
        <div className="mt-14" data-testid="google-maps-section">          <h3
            className="text-xl md:text-2xl tracking-tight font-medium mb-6 text-center"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
          >
            Find Us
          </h3>
          <div className="border border-[var(--mp-line)] rounded-sm overflow-hidden">
            <iframe
              src="https://maps.google.com/maps?q=MUTHA+PARAS+%26+CO+28+Venkusahuji+Lane+Chickpet+Bangalore+560053&t=&z=17&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Mutha Paras & Co. Location - Chickpet, Bangalore"
              data-testid="google-map-iframe"
              className="w-full h-[300px] sm:h-[400px]"
            />
          </div>
          <p
            className="text-center mt-3 text-sm"
            style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
          >
            <a
              href="https://maps.app.goo.gl/BfuGnnPuXTabFs1B8"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline"
              style={{ color: "var(--mp-text-strong)" }}
              data-testid="google-maps-link"
            >
              Open in Google Maps
            </a>
          </p>
        </div>

        <HowToReachUs />
      </div>
    </section>
  );
}
